/** Marks a disclosure that is still open while its height animates shut. */
const CLOSING_ATTR = 'data-closing';

/** The curve of --ease-soft; the chevron turns in step (vehicle-detail.css). */
const DURATION = 300;
const EASING = 'cubic-bezier(0.22, 0.9, 0.3, 1)';

const running = new WeakMap<HTMLDetailsElement, Animation>();

/** Opens `details` and collapses the other open disclosures in `group` — or collapses `details`. */
export function toggleDisclosure(details: HTMLDetailsElement, group: Element) {
    const expand = !isExpanded(details);
    if (expand) collapseOthers(details, group);
    animateDisclosure(details, expand);
}

function isExpanded(details: HTMLDetailsElement) {
    return details.open && !details.hasAttribute(CLOSING_ATTR);
}

function collapseOthers(details: HTMLDetailsElement, group: Element) {
    group.querySelectorAll('details').forEach((other) => {
        if (other !== details && isExpanded(other)) animateDisclosure(other, false);
    });
}

/** Starts from the current height, so a click mid-way turns the animation around. */
function animateDisclosure(details: HTMLDetailsElement, expand: boolean) {
    const from = details.getBoundingClientRect().height;
    stopAnimation(details);

    if (prefersReducedMotion()) {
        details.open = expand;
        return;
    }

    // Open while collapsing too, so the content stays until the end.
    details.open = true;
    details.toggleAttribute(CLOSING_ATTR, !expand);

    const to = expand ? details.getBoundingClientRect().height : collapsedHeight(details);
    const animation = details.animate(
        { height: [`${from}px`, `${to}px`] },
        { duration: DURATION, easing: EASING, fill: 'forwards' },
    );

    animation.onfinish = () => {
        details.open = expand;
        stopAnimation(details);
    };
    running.set(details, animation);
}

function stopAnimation(details: HTMLDetailsElement) {
    running.get(details)?.cancel();
    running.delete(details);
    details.removeAttribute(CLOSING_ATTR);
}

/** The closed height: the summary plus the border. */
function collapsedHeight(details: HTMLDetailsElement) {
    const summary = details.querySelector(':scope > summary');
    const { borderTopWidth, borderBottomWidth } = getComputedStyle(details);

    return (summary?.getBoundingClientRect().height ?? 0) + parseFloat(borderTopWidth) + parseFloat(borderBottomWidth);
}

function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
